/**
 * Message boxes, in the page.
 *
 * The desktop asked these through the operating system's own dialogs. The
 * browser's `confirm()` cannot name its buttons, and "OK / Cancel" for "Save
 * changes?" is how work gets lost, so each question is a native <dialog> with
 * buttons that say what they do. `showModal` gives focus trapping, Escape and a
 * backdrop for free.
 *
 * The rule carried over from the desktop: Escape always answers with the button
 * that changes nothing.
 */
import './dialogs.css'

export interface Ask {
  message: string
  detail?: string
  buttons: string[]
  /** The button Enter presses: focused when the dialog opens. */
  defaultId?: number
  /** The answer Escape gives; it must be the choice that changes nothing. */
  cancelId: number
  kind?: 'info' | 'warning' | 'error' | 'question'
  /** An image beside the message, for About. */
  icon?: string
}

let queue: Promise<unknown> = Promise.resolve()

/**
 * Shows one dialog and resolves with the index of the button chosen. Dialogs
 * asked while one is open wait their turn, as the desktop's did.
 */
export function ask(opts: Ask): Promise<number> {
  const run = (): Promise<number> => show(opts)
  const next = queue.then(run, run)
  queue = next.catch(() => {})
  return next
}

function show(opts: Ask): Promise<number> {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog')
    dialog.className = `pf-dialog pf-dialog--${opts.kind ?? 'info'}`
    dialog.setAttribute('aria-labelledby', 'pf-dialog-message')

    const body = document.createElement('div')
    body.className = 'pf-dialog__body'
    if (opts.icon) {
      const img = document.createElement('img')
      img.className = 'pf-dialog__icon'
      img.src = opts.icon
      img.alt = ''
      body.appendChild(img)
    }
    const text = document.createElement('div')
    const message = document.createElement('p')
    message.id = 'pf-dialog-message'
    message.className = 'pf-dialog__message'
    message.textContent = opts.message
    text.appendChild(message)
    if (opts.detail) {
      const detail = document.createElement('p')
      detail.className = 'pf-dialog__detail'
      detail.textContent = opts.detail
      text.appendChild(detail)
      dialog.setAttribute('aria-describedby', 'pf-dialog-detail')
      detail.id = 'pf-dialog-detail'
    }
    body.appendChild(text)
    dialog.appendChild(body)

    const row = document.createElement('div')
    row.className = 'pf-dialog__buttons'
    let answered = false
    const finish = (index: number): void => {
      if (answered) return
      answered = true
      dialog.close()
      dialog.remove()
      resolve(index)
    }
    const defaultId = opts.defaultId ?? 0
    const buttons = opts.buttons.map((label, i) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = i === defaultId ? 'pf-dialog__button is-default' : 'pf-dialog__button'
      b.textContent = label
      b.addEventListener('click', () => finish(i))
      row.appendChild(b)
      return b
    })
    dialog.appendChild(row)

    // Escape: the choice that changes nothing.
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault()
      finish(opts.cancelId)
    })
    // Keys pressed here are the dialog's: the app's shortcuts must not act
    // behind it.
    dialog.addEventListener('keydown', (e) => e.stopPropagation())

    document.body.appendChild(dialog)
    dialog.showModal()
    buttons[defaultId]?.focus()
  })
}

/** A message with only OK. */
export async function info(
  message: string,
  detail?: string,
  kind: Ask['kind'] = 'info'
): Promise<void> {
  await ask({ message, detail, buttons: ['OK'], cancelId: 0, kind })
}
